import React, { FC, useCallback, useEffect, useState } from 'react'
import { Tab } from '@headlessui/react'
import classNames from 'classnames'
import { AnimatePresence } from 'framer-motion'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useInView } from 'react-intersection-observer'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { slugs } from '@/constants/slugs'
import { useExperimentContext } from '@/experimentation'
import { useScrollToElement } from '@/hooks/useScrollToElement'
import { useToggle } from '@/hooks/useToggle'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { PhaseSlug, SweepstakeObject } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useTranslate } from '@/utils/translate/translate-client'
import { BackgroundLinearGradient } from '@/views/PreSales/PreSalesGiveawayView/components/BackgroundLinearGradient'
import { BackgroundVideo } from '@/views/PreSales/PreSalesGiveawayView/components/BackgroundVideo'
import { GiveawayFooter } from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayFooter'
import GiveawayNav from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayNav'
import { GiveawayTypographyProvider } from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayTypographyProvider'
import { FloatingCtaButton } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/FloatingCtaButton'
import { FloatingShareButton } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/FloatingShareButton'
import GiveawayHero from '@/views/PreSales/PreSalesGiveawayView/components/Hero/GiveawayHero'
import { LearnMoreButton } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/LearnMoreButton'
import { HowItWorks } from '@/views/PreSales/PreSalesGiveawayView/components/HowItWorks'
import { OverlayYoutubeVideo } from '@/views/PreSales/PreSalesGiveawayView/components/OverlayYouTubeVideo'
import { PartnerLogosSection } from '@/views/PreSales/PreSalesGiveawayView/components/PartnerLogosSection'
import { ShareGiveawayModal } from '@/views/PreSales/PreSalesGiveawayView/components/ShareGiveawayModal'
import { PresaleDetailsTab } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/PresaleDetailsTab'
import { PresalesFAQsTab } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/PresaleFAQsTab'
import { PresaleUpdatesTab } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/PresaleUpdatesTab'
import { PrizeItemsTab } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/PrizeItemsTab'
import { VacationTab } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/VacationTab'
import TicketPackSection from '@/views/PreSales/PreSalesGiveawayView/components/TicketPackSection'
import { GTabText } from '@/views/PreSales/PreSalesGiveawayView/components/Typography'
import { getTrailerExperimentValueLastRodeo } from '@/views/PreSales/PreSalesGiveawayView/config/trailerExperimentValues'
import {
  GiveawayTabEnum,
  useGiveawayConfigData,
} from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayConfigData'

export const vacationSlugs = new Set([slugs.braveTheDark, slugs.ruleBreakers])

interface Props {
  theatricalSlug: string
  tickets?: ShopifyProductWithBundles[]
  sweepstake: SweepstakeObject
  phase: PhaseSlug | null
}

const tabClassName =
  'rounded-none pb-[9px] text-giveaway-text-subtle-light hover:!text-black border-b-transparent border-b-[3px] ui-selected:border-giveaway-secondary-accent ui-selected:!text-giveaway-text-primary-light'

export const PreSalesGiveawayView: FC<Props> = ({ tickets, theatricalSlug, phase, sweepstake }) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isBackgroundVideoPaused, togglePauseBackgroundVideo] = useToggle(false)
  const [isShareModalOpen, setIsShareModalOpen] = useToggle(false)
  const [isScenePlayerOpen, setIsScenePlayerOpen] = useState(false)
  const { push, query } = useRouter()
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()

  const { ref: mobileProductRef, inView: isMobileProductsInView } = useInView({ threshold: 0 })
  const { ref: heroRef, inView: isHeroInView } = useInView({ threshold: 0.2 })
  const { ref: contentRef, scrollToElement: scrollToContent } = useScrollToElement<HTMLDivElement>()
  const { ref: tabsContainerRef, scrollToElement: scrollToTopTabs } = useScrollToElement<HTMLDivElement>()
  const { ref: mobileTicketContainerRef, scrollToElement: scrollToMobileElement } = useScrollToElement<HTMLDivElement>()
  const { ref: desktopTicketContainerRef, scrollToElement: scrollToDesktopElement } =
    useScrollToElement<HTMLDivElement>()
  const [experimentTrailerUrl, setExperimentTrailerUrl] = useState<string | null>(null)
  const { getFeatureValue } = useExperimentContext()

  const giveawayData = useGiveawayConfigData({ theatricalSlug })
  const sceneId = getItemOrFirstEntry(query?.sceneId)

  const activateTrailerExperiment = useCallback(() => {
    const trailerExperimentID = getFeatureValue('last-rodeo-giveaway-trailers-exp', 'video-1')
    if (!trailerExperimentID) return
    setExperimentTrailerUrl(getTrailerExperimentValueLastRodeo(trailerExperimentID))
  }, [getFeatureValue])

  useEffect(() => {
    if (query.share) {
      setIsShareModalOpen(true)
    }
  }, [query.share, setIsShareModalOpen])

  useEffect(() => {
    if (sceneId) {
      setIsScenePlayerOpen(true)
      togglePauseBackgroundVideo(true)
    }
  }, [sceneId, togglePauseBackgroundVideo])

  const scrollToProducts = useCallback(() => {
    scrollToMobileElement()
    scrollToDesktopElement()
  }, [scrollToDesktopElement, scrollToMobileElement])

  const scrollToTab = useCallback(
    (tabType: GiveawayTabEnum) => {
      const nextTabIndex = giveawayData?.tabs.findIndex((tab) => tab.type === tabType)

      if (nextTabIndex == null) return

      setSelectedIndex(nextTabIndex)
      scrollToTopTabs()
    },
    [giveawayData?.tabs, scrollToTopTabs],
  )

  const handleRefundInfoClick = useCallback(() => {
    scrollToTab(GiveawayTabEnum.Faq)
    push('#refundPolicy')
  }, [push, scrollToTab])

  if (!giveawayData) return null

  const { tabs, steps, partnerLogos, prizes, logoUrl, galleryImages, share, videoImage, inTheatersText, heroTitle } =
    giveawayData

  const {
    winnerAnnouncedDate,
    seoImageUrl,
    projectName,
    heroDesktopImageUrl,
    heroMobileImageUrl,
    backgroundVideoUrl,
    trailerYoutubeId,
    giveawayYoutubeId,
    rulesUrl,
    entryVariantId,
    status,
    value,
  } = sweepstake

  const isActive = status === 'LIVE'

  return (
    <GiveawayTypographyProvider theatricalSlug={theatricalSlug}>
      {theatricalSlug === slugs.braveTheDark && (
        <Head>
          <link rel="stylesheet" href="https://use.typekit.net/wuw2vxm.css" />
        </Head>
      )}

      {(theatricalSlug === slugs.ruleBreakers || theatricalSlug === 'rule-breakers-tech') && (
        <Head>
          <link rel="stylesheet" href="https://use.typekit.net/iak6fzg.css" />
        </Head>
      )}

      {sceneId && (
        <OverlayYoutubeVideo
          isOpen={isScenePlayerOpen}
          onClose={() => {
            setIsScenePlayerOpen(false)
            togglePauseBackgroundVideo(false)
          }}
          youtubeID={sceneId}
        />
      )}

      <div className={classNames(isScenePlayerOpen ? 'fixed' : 'static')} data-slug={theatricalSlug}>
        <Seo
          {...getBasicPageMetaData({
            cloudinaryImagePath: seoImageUrl ?? '',
            cloudinaryTransformation: 'b_rgb:000000,c_fill',
            title: t('giveawaySeoTitle', 'PRE-ORDER {{projectName}} Tickets | Angel Studios', {
              projectName,
            }),
            description: t('giveawaySeoDescription', 'Buy {{projectName}} tickets for a Chance to Win the Giveaway!', {
              projectName,
            }),
            locale,
            path: `/giveaway/${theatricalSlug}`,
          })}
        />

        <header
          ref={heroRef}
          className="relative flex min-h-dvh flex-col bg-black p-6 md:h-[856px] md:min-h-full md:bg-center"
        >
          {heroDesktopImageUrl && heroMobileImageUrl && (
            <ResponsiveBackgroundImage
              srcUrls={{
                xs: heroMobileImageUrl,
                sm: heroMobileImageUrl,
                md: heroDesktopImageUrl,
                lg: heroDesktopImageUrl,
                xl: heroDesktopImageUrl,
                '2xl': heroDesktopImageUrl,
              }}
              src={heroMobileImageUrl}
            />
          )}

          <BackgroundLinearGradient />
          {!!backgroundVideoUrl && <BackgroundVideo paused={isBackgroundVideoPaused} videoSrc={backgroundVideoUrl} />}
          <GiveawayNav theatricalSlug={theatricalSlug} />

          <GiveawayHero
            phase={phase}
            theatricalSlug={theatricalSlug}
            inTheatersText={inTheatersText}
            projectName={projectName}
            logoUrl={logoUrl}
            trailerId={experimentTrailerUrl ?? giveawayYoutubeId ?? trailerYoutubeId}
            title={heroTitle}
            winnerAnnouncedDate={winnerAnnouncedDate}
            onScrollToProducts={scrollToProducts}
            togglePauseBackgroundVideo={togglePauseBackgroundVideo}
            videoButtonText={
              giveawayYoutubeId
                ? t('watchGiveawayHeroVideo', 'Watch Giveaway Video')
                : t('watchVideoHeroGiveaway', 'Watch Video')
            }
            activateTrailerExperiment={activateTrailerExperiment}
          />

          <LearnMoreButton onClick={scrollToContent} className="relative z-10 mx-auto mt-auto hidden" />
        </header>

        {isActive && tickets && entryVariantId && (
          <TicketPackSection
            ref={mobileTicketContainerRef}
            className="w-full overflow-y-auto bg-white md:bg-[#F5F5F5] lg:hidden"
            onRefundInfoClick={handleRefundInfoClick}
            tickets={tickets}
            mobileProductRef={mobileProductRef}
            entriesProductId={entryVariantId}
            projectName={projectName}
            theatricalSlug={theatricalSlug}
          />
        )}

        <div ref={contentRef} className="px-4 md:px-12">
          {isActive && steps && (
            <HowItWorks
              className={partnerLogos && '!pb-10 md:!pb-[60px]'}
              scrollToProducts={scrollToProducts}
              steps={steps}
            />
          )}
          {partnerLogos && <PartnerLogosSection partnerLogos={partnerLogos} />}
        </div>

        <div ref={tabsContainerRef} className="mx-auto flex w-full bg-[#F5F5F5]">
          <div
            className={classNames(
              'relative min-h-screen w-full pb-10 lg:pb-0',
              isActive ? 'max-w-[960px] ml-auto' : 'max-w-[1440px] mx-auto',
            )}
          >
            <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
              <Tab.List className="sticky top-0 z-30 flex justify-center gap-8 border-b-2 border-[#D3D3D6] bg-[#F5F5F5] pb-4 pt-6">
                {theatricalSlug === slugs.ruleBreakers && (
                  <Tab disabled={!isActive} onClick={scrollToTopTabs} className={classNames(tabClassName)}>
                    <GTabText>{t('vacationPackagesGiveaway', 'Vacation Packages')}</GTabText>
                  </Tab>
                )}
                {tabs.map((tab) => {
                  return (
                    <Tab
                      disabled={tab.type === 'Details' && !isActive}
                      key={tab.title}
                      onClick={scrollToTopTabs}
                      className={classNames(
                        tabClassName,
                        tab.type === 'Details' &&
                          !isActive &&
                          '!opacity-70 cursor-not-allowed hover:!text-giveaway-subtle',
                      )}
                    >
                      <GTabText>{tab.title}</GTabText>
                    </Tab>
                  )
                })}
              </Tab.List>
              <Tab.Panels className="w-full">
                {theatricalSlug === slugs.ruleBreakers && (
                  <Tab.Panel className="w-full">
                    <VacationTab />
                  </Tab.Panel>
                )}
                {tabs.map((tab) => (
                  <Tab.Panel className="w-full" key={tab.title}>
                    {tab.type === GiveawayTabEnum.Details && (
                      <PresaleDetailsTab
                        theatricalSlug={theatricalSlug}
                        prizes={prizes}
                        trailerId={experimentTrailerUrl ?? giveawayYoutubeId ?? trailerYoutubeId}
                        galleryImages={galleryImages}
                        scrollToPrizes={() => scrollToTab(GiveawayTabEnum.Prizes)}
                        scrollToProducts={() => scrollToProducts()}
                        projectName={projectName}
                        videoImage={videoImage}
                        giveawayValue={value}
                        activateTrailerExperiment={activateTrailerExperiment}
                      />
                    )}
                    {tab.type === GiveawayTabEnum.Prizes && prizes && (
                      <PrizeItemsTab theatricalSlug={theatricalSlug} prizes={prizes} />
                    )}
                    {tab.type === GiveawayTabEnum.Updates && <PresaleUpdatesTab scroll={scrollToProducts} />}
                    {tab.type === GiveawayTabEnum.Faq && <PresalesFAQsTab theatricalSlug={theatricalSlug} />}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>

          {/*Desktop Ticket Pack Section*/}
          {isActive && tickets && entryVariantId && (
            <TicketPackSection
              projectName={projectName}
              theatricalSlug={theatricalSlug}
              ref={desktopTicketContainerRef}
              className="hidden w-full max-w-[480px] lg:mr-auto lg:block"
              onRefundInfoClick={handleRefundInfoClick}
              tickets={tickets}
              entriesProductId={entryVariantId}
            />
          )}
        </div>

        <GiveawayFooter
          theatricalSlug={theatricalSlug}
          isActive={isActive}
          logoUrl={logoUrl}
          scrollToTab={scrollToTab}
          scrollToProducts={scrollToProducts}
          tabs={tabs}
        />

        {isActive && (
          <AnimatePresence>
            <>
              {!isHeroInView && share && (
                <FloatingShareButton
                  onClick={() => setIsShareModalOpen(true)}
                  className={!isMobileProductsInView ? 'bottom-24 md:bottom-[1.5rem]' : 'bottom-[1.5rem]'}
                />
              )}

              {!isHeroInView && !isMobileProductsInView && <FloatingCtaButton onClick={scrollToProducts} />}
            </>
          </AnimatePresence>
        )}

        {share && (
          <ShareGiveawayModal
            theatricalSlug={theatricalSlug}
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            imageUrl={share.shareImage}
            shareText={share.shareText}
            rulesLink={rulesUrl}
          />
        )}
      </div>
    </GiveawayTypographyProvider>
  )
}
